import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';

const useStyles = makeStyles((theme) => ({
  formControlLabel: {
    "& .MuiFormControlLabel-label": {
      fontWeight: "bold",
    },
  },
}));

const StyledAccordion = withStyles({
  root: {
    paddingTop: 4,
    paddingBottom: 4,
    boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
    "&:not(:last-child)": { borderBottom: 0 },
    "&:before": { display: "none" },
    "&$expanded": { margin: "auto" },
  },
  expanded: {},
})(Accordion);

const StyledAccordionSummary = withStyles({
  root: {
    minHeight: 36,
    "&$expanded": { minHeight: 36 },
  },
  content: {
    margin: "0px 0",
    "&$expanded": { margin: "0px 0" },
  },
  expanded: {},
})(AccordionSummary);

const StyledAccordionDetails = withStyles({
  root: { padding: "unset" },
})(AccordionDetails);

function removeKeysRecursively(data, keysToRemove) {
  if (Array.isArray(data)) {
    return data.map((item) => removeKeysRecursively(item, keysToRemove));
  }
  if (typeof data === "object" && data !== null) {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (!keysToRemove.includes(key)) {
        acc[key] = removeKeysRecursively(value, keysToRemove);
      }
      return acc;
    }, {});
  }
  return data;
}

function initializeEnabledState(feats, enabledList) {
  const state = {};
  const enabledArray = Array.isArray(enabledList) ? enabledList : [];
  const processItem = (prefix, item) => {
    const itemName = prefix ? `${prefix}.${item.name}` : item.name;
    state[itemName] = enabledList ? enabledArray.includes(itemName) : (item.isEnabled ?? false);
    ["functions", "actions", "subActions", "subModules"].forEach((childKey) => {
      if (item[childKey]) {
        item[childKey].forEach((child) => processItem(itemName, child));
      }
    });
  };
  feats.forEach((module) => processItem("", module));
  return state;
}

const CompanyFeaturePanel = forwardRef(({ features, onDataChange, disabled, enabledList = null, layer = 3 }, ref) => {
  const classes = useStyles();
  const allKeys = ["module", "subModules", "functions", "actions", "subActions"];
  const keysToRemove = allKeys.slice(layer, 5);

  const [featuresTrimmed, setFeaturesTrimmed] = useState(() => removeKeysRecursively(features, keysToRemove));
  const [enabled, setEnabled] = useState(() => initializeEnabledState(featuresTrimmed, enabledList));

  useEffect(() => {
    const temp = removeKeysRecursively(features, keysToRemove);
    setFeaturesTrimmed(temp);
    setEnabled(initializeEnabledState(temp, enabledList));
  }, [features, layer, enabledList, keysToRemove]);

  const onChange = (name, isChecked, isFunction) => {
    const temp = { ...enabled };
    temp[name] = isChecked;

    // Update descendants
    Object.keys(temp).forEach((key) => {
      if (key.startsWith(`${name}.`) && key !== name) {
        temp[key] = isChecked;
      }
    });

    // Handle mutually exclusive read/write functions
    if (isFunction) {
      const parts = name.split(".");
      let functionIndex = -1;
      for (let i = 0; i < parts.length; i++) {
        if (parts[i] === "read" || parts[i] === "write") {
          functionIndex = i;
          break;
        }
      }
      if (functionIndex !== -1) {
        const currentFunction = parts[functionIndex];
        const oppositeFunction = currentFunction === "read" ? "write" : "read";
        const prefix = parts.slice(0, functionIndex).join(".");
        const oppositeKey = prefix ? `${prefix}.${oppositeFunction}` : oppositeFunction;
        Object.keys(temp).forEach((key) => {
          if (key === oppositeKey || key.startsWith(`${oppositeKey}.`)) {
            temp[key] = false;
          }
        });
      }
    }

    // Enable ancestors if checked
    if (isChecked) {
      name.split(".").forEach((_, i) => {
        const ancestorKey = name.split(".").slice(0, i + 1).join(".");
        temp[ancestorKey] = true;
      });
    }

    // Disable module if all siblings disabled
    if (!isChecked && name.split(".").length === 2) {
      const moduleName = name.split(".")[0];
      const isAllDisabled = Object.keys(temp).filter(
        (key) => key !== moduleName && key.startsWith(moduleName) && temp[key]
      ).length === 0;
      temp[moduleName] = !isAllDisabled;
    }

    setEnabled(temp);
    onDataChange(temp);
  };

  const selectAll = () => {
    const temp = { ...enabled };
    Object.keys(temp).forEach((key) => { temp[key] = true; });
    // Prefer write over read for mutually exclusive
    Object.keys(temp).forEach((key) => {
      if (key.endsWith(".read")) {
        const writeKey = key.replace(".read", ".write");
        if (temp[writeKey] !== undefined) {
          temp[key] = false;
          Object.keys(temp).forEach((k) => {
            if (k.startsWith(`${key}.`)) temp[k] = false;
          });
        }
      }
    });
    setEnabled(temp);
    onDataChange(temp);
  };

  const deselectAll = () => {
    const temp = { ...enabled };
    Object.keys(temp).forEach((key) => { temp[key] = false; });
    setEnabled(temp);
    onDataChange(temp);
  };

  useImperativeHandle(ref, () => ({ selectAll, deselectAll }));

  if (!featuresTrimmed || featuresTrimmed.length === 0) return null;

  return (
    <Box>
      {featuresTrimmed.map((module) => (
        <Box mb={1} key={module.name}>
          <StyledAccordion style={{ width: "100%" }}>
            <StyledAccordionSummary
              expandIcon={
                (module?.subModules?.length > 0 || module?.functions?.length > 0)
                  ? <ExpandMoreIcon /> : null
              }
            >
              <FormControlLabel
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={!!enabled[module.name]}
                    indeterminate={(() => {
                      if (!module.subModules) return false;
                      const enabledSubs = Object.keys(enabled).filter(
                        (f) => f.startsWith(`${module.name}.`) && f.split(".").length === 2 && enabled[f]
                      );
                      return enabledSubs.length > 0 && enabledSubs.length < module.subModules.length;
                    })()}
                  />
                }
                onChange={(e) => onChange(module.name, e.target.checked)}
                label={module.title}
                className={classes.formControlLabel}
              />
            </StyledAccordionSummary>
            {((module.functions && module.functions.length > 0) || (module.subModules && module.subModules.length > 0)) && (
              <StyledAccordionDetails>
                <Divider />
                <Box display="flex" flexDirection="column" py={1}>
                  {module.functions && module.functions.length > 0 ? (
                    <Box px={2} display="flex" flexDirection="column">
                      {module.functions.map((func) => (
                        <React.Fragment key={func.name}>
                          <FormControlLabel
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            control={
                              <Radio
                                disabled={disabled}
                                checked={!!enabled[`${module.name}.${func.name}`]}
                              />
                            }
                            onClickCapture={(e) => {
                              onChange(`${module.name}.${func.name}`, e.target.checked, true);
                            }}
                            label={func.title}
                            className={classes.formControlLabel}
                          />
                          {(func.actions ?? []).map((action) => (
                            <React.Fragment key={action.name}>
                              <FormControlLabel
                                style={{ marginLeft: 20 }}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                control={
                                  <Checkbox
                                    disabled={disabled}
                                    checked={!!enabled[`${module.name}.${func.name}.${action.name}`]}
                                  />
                                }
                                onClickCapture={(e) => {
                                  onChange(`${module.name}.${func.name}.${action.name}`, e.target.checked, true);
                                }}
                                label={action.title}
                                className={classes.formControlLabel}
                              />
                              {(action.subActions ?? []).map((subAction) => (
                                <FormControlLabel
                                  key={subAction.name}
                                  style={{ marginLeft: 40 }}
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.stopPropagation()}
                                  control={
                                    <Checkbox
                                      disabled={disabled}
                                      checked={!!enabled[`${module.name}.${func.name}.${action.name}.${subAction.name}`]}
                                    />
                                  }
                                  onClickCapture={(e) => {
                                    onChange(`${module.name}.${func.name}.${action.name}.${subAction.name}`, e.target.checked, true);
                                  }}
                                  label={subAction.title}
                                  className={classes.formControlLabel}
                                />
                              ))}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      ))}
                    </Box>
                  ) : (
                    <>
                      {module.subModules.map((subModule, index) => (
                        <Box px={2} key={subModule.name}>
                          <Grid container>
                            <Grid item xs={5}>
                              <Box display="flex" height="100%">
                                <Box flexGrow={1}>
                                  <FormControlLabel
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                    control={
                                      <Checkbox
                                        disabled={disabled}
                                        checked={!!enabled[`${module.name}.${subModule.name}`]}
                                      />
                                    }
                                    onChange={(e) => {
                                      onChange(`${module.name}.${subModule.name}`, e.target.checked);
                                    }}
                                    label={subModule.title}
                                    className={classes.formControlLabel}
                                  />
                                </Box>
                                {layer > 2 && <Divider flexItem orientation="vertical" />}
                              </Box>
                            </Grid>
                            <Grid item xs={7}>
                              <Box pl={1} display="flex" flexDirection="column">
                                {(subModule.functions ?? []).map((func) => (
                                  <React.Fragment key={func.name}>
                                    <FormControlLabel
                                      onClick={(e) => e.stopPropagation()}
                                      onFocus={(e) => e.stopPropagation()}
                                      control={
                                        <Radio
                                          disabled={disabled}
                                          checked={!!enabled[`${module.name}.${subModule.name}.${func.name}`]}
                                        />
                                      }
                                      onClickCapture={(e) => {
                                        onChange(`${module.name}.${subModule.name}.${func.name}`, e.target.checked, true);
                                      }}
                                      label={func.title}
                                      className={classes.formControlLabel}
                                    />
                                    {(func.actions ?? []).map((action) => (
                                      <React.Fragment key={action.name}>
                                        <FormControlLabel
                                          style={{ marginLeft: 20 }}
                                          onClick={(e) => e.stopPropagation()}
                                          onFocus={(e) => e.stopPropagation()}
                                          control={
                                            <Checkbox
                                              disabled={disabled}
                                              checked={!!enabled[`${module.name}.${subModule.name}.${func.name}.${action.name}`]}
                                            />
                                          }
                                          onClickCapture={(e) => {
                                            onChange(`${module.name}.${subModule.name}.${func.name}.${action.name}`, e.target.checked, true);
                                          }}
                                          label={action.title}
                                          className={classes.formControlLabel}
                                        />
                                        {(action.subActions ?? []).map((subAction) => (
                                          <FormControlLabel
                                            key={subAction.name}
                                            style={{ marginLeft: 40 }}
                                            onClick={(e) => e.stopPropagation()}
                                            onFocus={(e) => e.stopPropagation()}
                                            control={
                                              <Checkbox
                                                disabled={disabled}
                                                checked={!!enabled[`${module.name}.${subModule.name}.${func.name}.${action.name}.${subAction.name}`]}
                                              />
                                            }
                                            onClickCapture={(e) => {
                                              onChange(`${module.name}.${subModule.name}.${func.name}.${action.name}.${subAction.name}`, e.target.checked, true);
                                            }}
                                            label={subAction.title}
                                            className={classes.formControlLabel}
                                          />
                                        ))}
                                      </React.Fragment>
                                    ))}
                                  </React.Fragment>
                                ))}
                              </Box>
                            </Grid>
                          </Grid>
                          {layer > 2 && module.subModules.length - 1 > index && <Divider />}
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
              </StyledAccordionDetails>
            )}
          </StyledAccordion>
        </Box>
      ))}
    </Box>
  );
});

export default CompanyFeaturePanel;
